import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getApiBaseUrl } from '../apiBase';
import Logo from '../components/Logo';

const API = getApiBaseUrl();

function strengthLabel(password, t) {
    if (!password || password.length < 8) return { text: t('resetPassword.strengthWeak'), color: '#c62828' };
    const hasDigit = /[0-9]/.test(password);
    const hasLetter = /[a-zA-Zа-яА-Я]/.test(password);
    if (password.length >= 8 && hasDigit && hasLetter) return { text: t('resetPassword.strengthStrong'), color: '#2e7d32' };
    return { text: t('resetPassword.strengthMedium'), color: '#f9a825' };
}

export default function ResetPasswordPage() {
    const { t } = useTranslation('auth');
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get('token') || '';
    const email = params.get('email') || '';

    const [checking, setChecking] = useState(true);
    const [valid, setValid] = useState(false);
    const [tokenMessage, setTokenMessage] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [countdown, setCountdown] = useState(3);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!token || !email) {
            setChecking(false);
            setValid(false);
            setTokenMessage(t('resetPassword.invalidLink'));
            return;
        }
        (async () => {
            try {
                const res = await axios.get(`${API}/auth/verify-reset-token`, {
                    params: { token, email }
                });
                setValid(!!res.data.valid);
                setTokenMessage(res.data.message || t('resetPassword.invalidLink'));
            } catch {
                setValid(false);
                setTokenMessage(t('resetPassword.invalidLink'));
            } finally {
                setChecking(false);
            }
        })();
    }, [token, email, t]);

    useEffect(() => {
        if (!success) return;
        if (countdown <= 0) {
            navigate('/login', { replace: true });
            return;
        }
        const timerId = setTimeout(() => setCountdown(c => c - 1), 1000);
        return () => clearTimeout(timerId);
    }, [success, countdown, navigate]);

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');
        if (password !== confirm) {
            setError(t('errors.passwordsMismatch'));
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/auth/reset-password`, {
                token,
                email,
                newPassword: password,
                confirmPassword: confirm
            });
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.message || t('errors.changeFailed'));
        } finally {
            setLoading(false);
        }
    }

    const strength = strengthLabel(password, t);

    if (checking) {
        return (
            <div className="sp-auth-page" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <p>{t('resetPassword.checkingLink')}</p>
            </div>
        );
    }

    return (
        <div className="sp-auth-page page-login">
            <div className="sp-auth-form-panel" style={{ width: '100%', justifyContent: 'center' }}>
                <div style={{ width: '100%', maxWidth: 420, margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: 24 }}>
                        <Logo variant="light" height={40} style={{ margin: '0 auto 24px' }} />
                    </div>
                    <div className="sp-data-card" style={{ padding: 36 }}>
                        {success ? (
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
                                <h2 style={{ margin: '0 0 12px' }}>{t('resetPassword.successTitle')}</h2>
                                <p style={{ color: '#64748b' }}>
                                    {t('resetPassword.redirectIn', { count: countdown })}
                                </p>
                            </div>
                        ) : !valid ? (
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
                                <p style={{ marginBottom: 20 }}>{tokenMessage}</p>
                                <Link to="/forgot-password" className="sp-btn sp-btn--primary" style={{ textDecoration: 'none', display: 'inline-block' }}>
                                    {t('resetPassword.requestNewLink')}
                                </Link>
                            </div>
                        ) : (
                            <>
                                <h1 style={{ margin: '0 0 8px', fontSize: 20, textAlign: 'center' }}>
                                    {t('resetPassword.newPasswordFor', { email })}
                                </h1>
                                <form onSubmit={handleSubmit}>
                                    <label style={{ fontSize: 13, fontWeight: 600 }}>{t('resetPassword.newPassword')}</label>
                                    <div style={{ position: 'relative', marginBottom: 12 }}>
                                        <input
                                            className="sp-input"
                                            type={showPwd ? 'text' : 'password'}
                                            value={password}
                                            onChange={e => setPassword(e.target.value)}
                                            required
                                            style={{ paddingRight: 44 }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPwd(s => !s)}
                                            style={{
                                                position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                                                border: 'none', background: 'none', cursor: 'pointer', fontSize: 18
                                            }}
                                            aria-label={t('resetPassword.showPassword')}
                                        >
                                            {showPwd ? '🙈' : '👁'}
                                        </button>
                                    </div>
                                    <p style={{ fontSize: 13, color: strength.color, margin: '0 0 16px' }}>{strength.text}</p>

                                    <label style={{ fontSize: 13, fontWeight: 600 }}>{t('resetPassword.confirmPassword')}</label>
                                    <input
                                        className="sp-input"
                                        type={showPwd ? 'text' : 'password'}
                                        value={confirm}
                                        onChange={e => setConfirm(e.target.value)}
                                        required
                                        style={{ marginBottom: 20 }}
                                    />

                                    {error && (
                                        <div style={{ background: '#fce4ec', color: '#c62828', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
                                            {error}
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="sp-btn sp-btn--primary"
                                        style={{ width: '100%' }}
                                    >
                                        {loading ? t('resetPassword.saving') : t('resetPassword.savePassword')}
                                    </button>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
